/**
 * AddToFavouritesModal — 2-tab modal for adding a product to a favourite list
 *
 * Tab 1: "Select list" — dropdown of existing lists
 * Tab 2: "New list"    — text input for a new list name
 *
 * "Add to favourites" button greyed until valid selection/name entered,
 * then turns blue. Calls onSuccess callback with toast message.
 *
 * @module components/favourites/AddToFavouritesModal
 * @since 2026-05-25
 */
import { useState } from 'react';
import { useFavourites } from '@contexts/FavouritesContext';

export default function AddToFavouritesModal({ product, isOpen, onClose, onSuccess }) {
  const { lists, createList, addItem } = useFavourites();
  const [activeTab, setActiveTab] = useState('select'); // 'select' | 'new'
  const [selectedListId, setSelectedListId] = useState('');
  const [newListName, setNewListName] = useState('');

  if (!isOpen || !product) return null;

  const canSubmit = activeTab === 'select'
    ? selectedListId !== ''
    : newListName.trim().length > 0;

  function handleSubmit() {
    if (!canSubmit) return;

    if (activeTab === 'new') {
      // Create list first, then add item
      createList(newListName.trim());
      // We need the new list id — the context creates it with generateId()
      // Since we can't get the id synchronously, we'll add after a tick
      setTimeout(() => {
        // Get the most recently created list (it's prepended)
        const stored = JSON.parse(localStorage.getItem('mrt_favourites') || '{"lists":[]}');
        const newest = stored.lists[0];
        if (newest) {
          addItem(newest.id, product);
        }
        onSuccess?.('Product successfully added to the favourites');
        handleClose();
      }, 50);
      return;
    }

    // Select existing list
    addItem(selectedListId, product);
    onSuccess?.('Product successfully added to the favourites');
    handleClose();
  }

  function handleClose() {
    setActiveTab('select');
    setSelectedListId('');
    setNewListName('');
    onClose();
  }

  const tabs = [
    { key: 'select', label: 'Select list' },
    { key: 'new', label: 'New list' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-carbon-black/40 z-50" onClick={handleClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-start justify-between p-6 pb-0">
            <h2 className="font-sandvik-headline text-2xl font-normal text-carbon-black">
              Add to your favourite list
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="text-carbon-black hover:text-n5 transition-colors"
              aria-label="Close"
            >
              <span className="material-symbols-outlined text-2xl">close</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="px-6 mt-4 border-b border-n2">
            <nav className="flex gap-0" role="tablist">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  role="tab"
                  aria-selected={activeTab === tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-4 py-2.5 text-sm font-sandvik-text transition-colors relative
                    ${activeTab === tab.key
                      ? 'text-carbon-black font-medium bg-n1'
                      : 'text-n5 hover:text-carbon-black'}`}
                >
                  {tab.label}
                  {activeTab === tab.key && (
                    <span className="absolute top-0 left-0 right-0 h-0.5 bg-electric-blue" />
                  )}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab content */}
          <div className="p-6 bg-n1 min-h-[160px]">
            {activeTab === 'select' && (
              <div>
                <p className="text-sm font-sandvik-text font-medium text-carbon-black mb-3">
                  Select from your existing list
                </p>
                <select
                  value={selectedListId}
                  onChange={e => setSelectedListId(e.target.value)}
                  className="w-full px-3 py-2.5 border border-n3 text-sm
                             font-sandvik-text text-carbon-black bg-white
                             focus:outline-none focus:border-electric-blue"
                >
                  <option value="">Select an option</option>
                  {lists.map(list => (
                    <option key={list.id} value={list.id}>{list.name}</option>
                  ))}
                </select>
              </div>
            )}

            {activeTab === 'new' && (
              <div>
                <p className="text-sm font-sandvik-text font-medium text-carbon-black mb-3">
                  Create your new list
                </p>
                <input
                  type="text"
                  value={newListName}
                  onChange={e => setNewListName(e.target.value)}
                  placeholder="Enter Wishlist Name"
                  className="w-full px-3 py-2.5 border border-n3 text-sm
                             font-sandvik-text text-carbon-black bg-white
                             placeholder:text-n4
                             focus:outline-none focus:border-electric-blue"
                  autoFocus
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-2.5 border border-n3 text-sm font-sandvik-text
                         text-carbon-black bg-n1 hover:bg-n2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className={`px-6 py-2.5 text-sm font-sandvik-text text-white transition-colors
                ${canSubmit
                  ? 'bg-electric-blue hover:bg-eb-3 cursor-pointer'
                  : 'bg-n3 cursor-not-allowed'}`}
            >
              Add to favourites
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
